import UserInformation from './models/UserInformation'
import { fetchUserInformation, fetchUserShopPoints } from './api'
import { getUserMemberInfo } from '@/views/points/api'
import UserShopPoints from './models/UserShopPoints'
import UserMemberInfo from '@/views/points/models/UserMemberInfo'
import RestaurantInfoManager from '@/views/restaurant-information/utils/restaurant-info-manager'

export default class AccountManager {
  private static _manager?: AccountManager

  static instance(): AccountManager {
    if (this._manager === undefined) {
      this._manager = new AccountManager()
    }

    return this._manager
  }

  private _userInformation?: UserInformation
  private _userMemberInfo?: UserMemberInfo

  private constructor() {
    this._userInformation = undefined
  }

  set userInformation(information: UserInformation) {
    this._userInformation = information
  }

  get userMemberInfo(): UserMemberInfo | undefined {
    return this._userMemberInfo
  }

  async fetchUserInformation(): Promise<UserInformation> {
    if (this._userInformation !== undefined) {
      return Promise.resolve(this._userInformation)
    } else {
      try {
        const { data } = await fetchUserInformation()
        const userMemberInfo = await this.fetchUserMemberInfo() // 可能返回undefined
        const {
          id,
          name,
          phoneNumber: phone,
          email,
          address: userAddresses,
          gender,
          birthday
        } = data
        this._userInformation = {
          id,
          name,
          phone,
          email,
          userAddresses,
          userMemberInfo,
          gender,
          birthday
        }
        return Promise.resolve(this._userInformation)
      } catch (e) {
        return Promise.reject(e)
      }
    }
  }

  /**
   * 获取用户会员信息
   * @param restaurantId - 餐厅ID，如果未提供则从 RestaurantInfoManager 获取
   * @returns 用户会员信息，如果没有有效的restaurantId则返回undefined
   */
  async fetchUserMemberInfo(restaurantId?: string): Promise<UserMemberInfo | undefined> {
    if (this._userMemberInfo !== undefined) {
      return Promise.resolve(this._userMemberInfo)
    } else {
      try {
        const finalRestaurantId = restaurantId || RestaurantInfoManager.instance().shopId
        // 如果没有有效的restaurantId，返回undefined
        if (!finalRestaurantId) {
          console.warn('无法获取用户会员信息：缺少restaurantId')
          return undefined
        }
        const { data } = await getUserMemberInfo(finalRestaurantId)
        this._userMemberInfo = data
        return Promise.resolve(this._userMemberInfo)
      } catch (e) {
        return Promise.reject(e)
      }
    }
  }

  private _userShopPoints: { [shopId: string]: UserShopPoints } = {}

  /**
   * 获取用户在指定店铺的积分信息(非circle eats渠道使用)
   * @param shopId 店铺ID
   * @returns 用户在指定店铺的积分信息
   */
  async fetchUserShopPoints(shopId: string): Promise<UserShopPoints> {
    const userShopPoints = this._userShopPoints[shopId]

    if (userShopPoints !== undefined) {
      return Promise.resolve(userShopPoints)
    } else {
      try {
        const { data } = await fetchUserShopPoints(shopId)
        this._userShopPoints[shopId] = data
        return Promise.resolve(data)
      } catch (e) {
        return Promise.reject(e)
      }
    }
  }

  reset(): void {
    this._userShopPoints = {}
    this._userInformation = undefined
  }
}
